import { User, EmailAuthProvider, reauthenticateWithCredential, updateEmail, updatePassword, updateProfile } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFirestoreDb, getFirebaseStorage } from '../firebase-client';
import { UserProfile, UserProfileUpdateData } from '../../types/user';
import { calculateProfileCompleteness } from '../../utils/profileUtils';
import { formatAuthError } from './authErrors';

/**
 * Creates a new user profile in Firestore
 * @param userId User ID
 * @param profileData Initial profile data
 * @returns Created user profile
 */
export const createUserProfile = async (userId: string, profileData: Partial<UserProfile>): Promise<UserProfile> => {
  // Get Firestore instance
  const db = getFirestoreDb();
  if (!db) {
    throw new Error('Firestore is not available');
  }

  try {
    const userRef = doc(db, 'users', userId);

    // Default profile values
    const defaultProfile: UserProfile = {
      uid: userId,
      email: profileData.email || '',
      displayName: profileData.displayName || '',
      photoURL: profileData.photoURL || '',
      subscriptionStatus: 'inactive',
      createdAt: new Date().toISOString(),
      authProvider: profileData.authProvider || 'email',
      isEmailVerified: profileData.isEmailVerified || false,
    };

    // Merge provided data with defaults
    const newProfile = {
      ...defaultProfile,
      ...profileData,
    };

    // Calculate profile completeness
    newProfile.profileCompleteness = calculateProfileCompleteness(newProfile);

    // Create profile document
    await setDoc(userRef, newProfile);

    return newProfile;
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

/**
 * Gets a user profile from Firestore
 * @param userId User ID
 * @returns User profile or null if not found
 */
export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  // Get Firestore instance
  const db = getFirestoreDb();
  if (!db) {
    console.warn('Firestore is not available for getUserProfile');
    return null;
  }

  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return null;
    }

    const userData = userDoc.data() as UserProfile;
    return userData;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

/**
 * Updates a user profile in Firestore
 * @param userId User ID
 * @param updateData Profile data to update
 */
export const updateUserProfile = async (
  userId: string,
  updateData: Partial<UserProfile>
): Promise<void> => {
  const currentProfile = await getUserProfile(userId);

  if (!currentProfile) {
    throw new Error(`No profile found for user ${userId}`);
  }

  const updatedProfile: UserProfile = {
    ...currentProfile,
    ...updateData,
    uid: userId,
    email: updateData.email ?? currentProfile.email,
    displayName: updateData.displayName ?? currentProfile.displayName,
    photoURL: updateData.photoURL ?? currentProfile.photoURL,
  };

  // Write updatedProfile to Firestore, API, or wherever needed
  await updateUserProfileInDatabase(userId, updatedProfile);
};

/**
 * Updates user's Firebase Auth profile (name, photo)
 * @param user Firebase user
 * @param displayName Display name
 * @param photoURL Photo URL
 */
export const updateUserAuthProfile = async (
  user: User,
  displayName?: string | null,
  photoURL?: string | null
): Promise<void> => {
  // Get Firestore instance
  const db = getFirestoreDb();
  // db check happens below, proceed with auth update first

  try {
    const updateData: { displayName?: string | null; photoURL?: string | null } = {};

    if (displayName !== undefined) {
      updateData.displayName = displayName;
    }

    if (photoURL !== undefined) {
      updateData.photoURL = photoURL;
    }

    // Update Firebase Auth profile
    await updateProfile(user, updateData);

    // Also update in Firestore if available
    if (db) {
      const userRef = doc(db, 'users', user.uid);
      // Note: getUserProfile and createUserProfile handle db instance internally
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        // Update existing document
        await updateDoc(userRef, updateData);
      } else {
        // Create new user profile with auth data
        console.log(`Creating new user profile for user ${user.uid} during auth profile update`);
        // Get the provider ID and map it to our valid auth provider types
        const providerId = user.providerData[0]?.providerId || 'password';
        let authProvider: 'email' | 'google' | 'facebook' = 'email';

        if (providerId.includes('google')) {
          authProvider = 'google';
        } else if (providerId.includes('facebook')) {
          authProvider = 'facebook';
        }

        // Convert null values to undefined to satisfy TypeScript
        const cleanedUpdateData = {
          ...updateData,
          displayName: updateData.displayName === null ? undefined : updateData.displayName,
          photoURL: updateData.photoURL === null ? undefined : updateData.photoURL,
        };

        await createUserProfile(user.uid, {
          ...cleanedUpdateData,
          email: user.email || '',
          isEmailVerified: user.emailVerified,
          authProvider: authProvider,
        });
      }
    } else {
      console.warn('Firestore not available, skipping profile update in DB for updateUserAuthProfile');
    }
  } catch (error) {
    console.error('Error updating auth profile:', error);
    throw error;
  }
};

/**
 * Updates user's email address
 * @param user Firebase user
 * @param newEmail New email address
 * @param password Current password (required for sensitive operations)
 */
export const updateUserEmail = async (
  user: User,
  newEmail: string,
  password?: string
): Promise<void> => {
  // Get Firestore instance
  const db = getFirestoreDb();
  // db check happens below, proceed with auth update first

  try {
    if (password) {
      // Reauthenticate user before email change
      const credential = EmailAuthProvider.credential(user.email!, password);
      await reauthenticateWithCredential(user, credential);
    }

    await updateEmail(user, newEmail);

    // Update email in Firestore if available
    if (db) {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        email: newEmail,
        isEmailVerified: false, // Reset verification status
      });
    } else {
      console.warn('Firestore not available, skipping email update in DB for updateUserEmail');
    }
  } catch (error: any) {
    console.error('Error updating email:', error);
    throw formatAuthError(error);
  }
};

/**
 * Updates user's password
 * @param user Firebase user
 * @param currentPassword Current password
 * @param newPassword New password
 */
export const updateUserPassword = async (
  user: User,
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  // This function only interacts with Auth, doesn't need db/storage instance directly
  try {
    // Reauthenticate user before password change
    const credential = EmailAuthProvider.credential(user.email!, currentPassword);
    await reauthenticateWithCredential(user, credential);

    await updatePassword(user, newPassword);
  } catch (error: any) {
    console.error('Error updating password:', error);
    throw formatAuthError(error);
  }
};

/**
 * Uploads an avatar image to Firebase Storage and updates user profile
 * @param user Firebase user
 * @param file Image file
 * @returns URL of uploaded image
 */
export const updateUserAvatar = async (user: User, file: File): Promise<string> => {
  // Get Storage instance
  const storage = getFirebaseStorage();
  if (!storage) {
    throw new Error('Firebase Storage is not available');
  }

  // Get Firestore instance (needed for updateUserAuthProfile call)
  const db = getFirestoreDb();
  // db check is handled within updateUserAuthProfile

  try {
    // Create a reference to the file in Firebase Storage
    const fileExtension = file.name.split('.').pop();
    const storageRef = ref(storage, `avatars/${user.uid}_${Date.now()}.${fileExtension}`);

    // Upload the file
    const snapshot = await uploadBytes(storageRef, file);

    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    // Update user profile (Auth and Firestore)
    // Note: updateUserAuthProfile handles its own db check
    await updateUserAuthProfile(user, null, downloadURL);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading avatar:', error);
    throw error;
  }
};

/**
 * Updates user's subscription status
 * @param userId User ID
 * @param status New subscription status
 * @param subscriptionDetails Optional subscription details
 */
export const updateSubscriptionStatus = async (
  userId: string,
  status: UserProfile['subscriptionStatus'],
  subscriptionDetails?: UserProfile['subscriptionDetails']
): Promise<void> => {
  // Get Firestore instance
  const db = getFirestoreDb();
  if (!db) {
    throw new Error('Firestore is not available');
  }

  try {
    const userRef = doc(db, 'users', userId);

    const updateData: any = {
      subscriptionStatus: status,
    };

    if (subscriptionDetails) {
      updateData.subscriptionDetails = subscriptionDetails;
    }

    await updateDoc(userRef, updateData);
  } catch (error) {
    console.error('Error updating subscription status:', error);
    throw error;
  }
};

/**
 * Updates user's last active timestamp
 * @param userId User ID
 */
export const updateLastActive = async (userId: string): Promise<void> => {
  // Get Firestore instance
  const db = getFirestoreDb();
  // Skip if Firestore is not available (SSR or init error)
  if (!db) {
    console.warn('Firestore not available, skipping updateLastActive');
    return;
  }

  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      lastActive: new Date().toISOString()
    });
  } catch (error) {
    // Just log error but don't throw, since this is a non-critical operation
    console.error('Error updating last active timestamp:', error);
  }
};

/**
 * Updates a user profile in Firestore
 * @param userId User ID
 * @param updatedProfile Updated profile data
 */
export async function updateUserProfileInDatabase(userId: string, updatedProfile: UserProfile): Promise<void> {
  const db = getFirestoreDb();
  if (!db) {
    throw new Error('Firestore is not available');
  }

  const userRef = doc(db, 'users', userId);

  // Convert UserProfile to a plain object that Firestore's updateDoc can accept
  // This removes the type error with AddPrefixToKeys
  const profileData = { ...updatedProfile };
  await updateDoc(userRef, profileData as any);
}
